import React from 'react';
import { useAuth } from './AuthContext';

type FontSizePreset = 'sm' | 'md' | 'lg' | 'xl';
type FontFamilyPreset =
  | 'plus_jakarta_sans'
  | 'inter'
  | 'roboto'
  | 'poppins'
  | 'lato'
  | 'merriweather'
  | 'open_sans'
  | 'montserrat'
  | 'nunito'
  | 'source_sans_3'
  | 'playfair_display';

const PRESET_TO_PERCENT: Record<FontSizePreset, number> = {
  sm: 90,
  md: 100,
  lg: 110,
  xl: 120,
};

type UiGlobalSettings = {
  fontSizePreset: FontSizePreset;
  fontFamily: FontFamilyPreset;
};

const FONT_FAMILY_TO_STACK: Record<FontFamilyPreset, string> = {
  plus_jakarta_sans:
    '"Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  inter:
    '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  roboto:
    '"Roboto", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  poppins:
    '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  lato:
    '"Lato", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  merriweather:
    '"Merriweather", Georgia, Cambria, "Times New Roman", Times, serif',
  open_sans:
    '"Open Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  montserrat:
    '"Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  nunito:
    '"Nunito", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  source_sans_3:
    '"Source Sans 3", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  playfair_display:
    '"Playfair Display", Georgia, Cambria, "Times New Roman", Times, serif',
};

type FontSizeContextValue = {
  effectivePreset: FontSizePreset;
  globalPreset: FontSizePreset;
  userPreset: FontSizePreset;
  useGlobal: boolean;
  setUserPreset: (preset: FontSizePreset) => Promise<void>;
  setUseGlobal: (useGlobal: boolean) => Promise<void>;
  setGlobalPreset: (preset: FontSizePreset) => Promise<void>;
  globalFontFamily: FontFamilyPreset;
  setGlobalFontFamily: (preset: FontFamilyPreset) => Promise<void>;
  canSetGlobal: boolean;
};

const FontSizeContext = React.createContext<FontSizeContextValue | null>(null);

function isPreset(v: unknown): v is FontSizePreset {
  return v === 'sm' || v === 'md' || v === 'lg' || v === 'xl';
}

function isFontFamily(v: unknown): v is FontFamilyPreset {
  return (
    v === 'plus_jakarta_sans'
    || v === 'inter'
    || v === 'roboto'
    || v === 'poppins'
    || v === 'lato'
    || v === 'merriweather'
    || v === 'open_sans'
    || v === 'montserrat'
    || v === 'nunito'
    || v === 'source_sans_3'
    || v === 'playfair_display'
  );
}

export function FontSizeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, authorizedFetch, fetchPreferences, updatePreferences, auth } = useAuth();
  const canSetGlobal = auth.type === 'ADMIN' || (auth.permissions || []).includes('UI_SETTING:UPDATE');
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const [globalPreset, setGlobalPresetState] = React.useState<FontSizePreset>('md');
  const [globalFontFamily, setGlobalFontFamilyState] = React.useState<FontFamilyPreset>('plus_jakarta_sans');
  const [userPreset, setUserPresetState] = React.useState<FontSizePreset>('md');
  const [useGlobal, setUseGlobalState] = React.useState<boolean>(true);

  const effectivePreset: FontSizePreset = useGlobal ? globalPreset : userPreset;

  const loadGlobal = React.useCallback(async () => {
    const res = await globalAuthorizedFetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}/settings/ui`);
    if (!res.ok) return;
    const data = (await res.json()) as Partial<UiGlobalSettings>;
    if (data.fontSizePreset && isPreset(data.fontSizePreset)) {
      setGlobalPresetState(data.fontSizePreset);
    }
    if (data.fontFamily && isFontFamily(data.fontFamily)) {
      setGlobalFontFamilyState(data.fontFamily);
    }
  }, [globalAuthorizedFetch]);

  const loadUser = React.useCallback(async () => {
    const prefs = await fetchPreferences();
    if (!prefs) return;
    if (prefs.fontSizePreset && isPreset(prefs.fontSizePreset)) {
      setUserPresetState(prefs.fontSizePreset);
    }
    if (typeof prefs.useGlobalFontSize === 'boolean') {
      setUseGlobalState(prefs.useGlobalFontSize);
    }
  }, [fetchPreferences]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      // Reset to default when logged out.
      document.documentElement.style.fontSize = '100%';
      document.documentElement.style.fontFamily = FONT_FAMILY_TO_STACK.plus_jakarta_sans;
      setGlobalPresetState('md');
      setGlobalFontFamilyState('plus_jakarta_sans');
      setUserPresetState('md');
      setUseGlobalState(true);
      return;
    }

    void loadGlobal();
    void loadUser();
  }, [isAuthenticated, loadGlobal, loadUser]);

  React.useEffect(() => {
    const percent = PRESET_TO_PERCENT[effectivePreset] ?? 100;
    document.documentElement.style.fontSize = `${percent}%`;
  }, [effectivePreset]);

  React.useEffect(() => {
    const stack = FONT_FAMILY_TO_STACK[globalFontFamily] ?? FONT_FAMILY_TO_STACK.plus_jakarta_sans;
    document.documentElement.style.fontFamily = stack;
  }, [globalFontFamily]);

  const setUserPreset = React.useCallback(async (preset: FontSizePreset) => {
    setUserPresetState(preset);
    await updatePreferences({ fontSizePreset: preset });
  }, [updatePreferences]);

  const setUseGlobal = React.useCallback(async (next: boolean) => {
    setUseGlobalState(next);
    await updatePreferences({ useGlobalFontSize: next });
  }, [updatePreferences]);

  const setGlobalPreset = React.useCallback(async (preset: FontSizePreset) => {
    if (!canSetGlobal) return;
    const res = await globalAuthorizedFetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}/settings/ui`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fontSizePreset: preset }),
    });
    if (!res.ok) return;
    setGlobalPresetState(preset);
  }, [canSetGlobal, globalAuthorizedFetch]);

  const setGlobalFontFamily = React.useCallback(async (preset: FontFamilyPreset) => {
    if (!canSetGlobal) return;
    const res = await globalAuthorizedFetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}/settings/ui`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fontSizePreset: globalPreset, fontFamily: preset }),
    });
    if (!res.ok) return;
    setGlobalFontFamilyState(preset);
  }, [canSetGlobal, globalAuthorizedFetch, globalPreset]);

  const value = React.useMemo<FontSizeContextValue>(() => ({
    effectivePreset,
    globalPreset,
    userPreset,
    useGlobal,
    setUserPreset,
    setUseGlobal,
    setGlobalPreset,
    globalFontFamily,
    setGlobalFontFamily,
    canSetGlobal,
  }), [
    effectivePreset,
    globalPreset,
    userPreset,
    useGlobal,
    setUserPreset,
    setUseGlobal,
    setGlobalPreset,
    globalFontFamily,
    setGlobalFontFamily,
    canSetGlobal,
  ]);

  return (
    <FontSizeContext.Provider value={value}>
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const ctx = React.useContext(FontSizeContext);
  if (!ctx) {
    throw new Error('useFontSize must be used within FontSizeProvider');
  }
  return ctx;
}

